import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { INDUSTRIES, SERVICES, CONTACT } from '../../site/nav';

// The site header. One menu: who we build for on the left, the pieces of
// the furniture platform on the right. Pricing and FAQ scroll on the home
// page and go home first from any other page.
export default function SiteNav() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const section = (id) => (pathname === '/' ? `#${id}` : `/#${id}`);
  const platform = SERVICES.filter((s) => !s.group);
  const onIndustry = (i) => pathname === i.path;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMenuOpen(false); }, [pathname]);

  return (
    // With the phone menu open the bar goes solid white and above every
    // hero layer, so the links are readable and tappable at the top of the
    // page too (not only after a scroll gives the bar its background).
    <nav className={`fixed top-0 w-full transition-all duration-300 ${open ? 'z-[70] bg-white border-b border-slate-200' : scrolled ? 'z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200' : 'z-40'}`}>
      <div className="w-full max-w-7xl 2xl:max-w-[88rem] mx-auto px-4 py-3 md:py-4">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2 md:space-x-3">
            <img src="/e.png" alt="Exitium" className="w-8 h-8 md:w-10 md:h-10 object-contain" />
            <span className="text-xl md:text-2xl font-bold tracking-tight">EXITIUM</span>
          </Link>

          <div className="hidden md:flex items-center space-x-7 lg:space-x-8">
            <div className="relative" onMouseEnter={() => setMenuOpen(true)} onMouseLeave={() => setMenuOpen(false)}>
              <button type="button" onClick={() => setMenuOpen((v) => !v)} aria-expanded={menuOpen} className="flex items-center gap-1 text-slate-600 hover:text-slate-900 transition-colors">
                Who we build for
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${menuOpen ? 'rotate-180' : ''}`} />
              </button>
              {menuOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[40rem] animate-fadeIn">
                  <div className="rounded-2xl border border-slate-200 bg-white shadow-xl p-2 grid grid-cols-[1.15fr_1fr]">
                    <div className="p-1">
                      <p className="px-3 pt-1.5 pb-1 text-slate-400 text-[10px] font-semibold uppercase tracking-[0.22em]">Industries</p>
                      {INDUSTRIES.map((i) => {
                        const Icon = i.icon;
                        return (
                          <Link key={i.id} to={i.path} className={`flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-blue-50 transition-colors ${onIndustry(i) ? 'bg-blue-50' : ''}`}>
                            <span className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center">
                              <Icon className="w-4 h-4 text-blue-600" />
                            </span>
                            <span className="min-w-0">
                              <span className="block text-sm font-semibold text-slate-900">{i.name}</span>
                              <span className="block text-xs text-slate-500 leading-snug">{i.outcome}</span>
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                    <div className="p-1 border-l border-slate-100">
                      <p className="px-3 pt-1.5 pb-1 text-slate-400 text-[10px] font-semibold uppercase tracking-[0.22em]">The furniture platform</p>
                      {platform.map((s) => {
                        const Icon = s.icon;
                        return (
                          <Link key={s.slug} to={s.path} className={`flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-blue-50 transition-colors ${pathname === s.path ? 'bg-blue-50' : ''}`}>
                            <Icon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            <span className="text-sm text-slate-700">{s.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Link to="/websites#work" className="text-slate-600 hover:text-slate-900 transition-colors">Our work</Link>
            <a href={section('pricing')} className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</a>
            <a href={section('faq')} className="text-slate-600 hover:text-slate-900 transition-colors">FAQ</a>
            <Link to="/dashboard" className="text-slate-600 hover:text-slate-900 transition-colors">Dashboard</Link>
            <a href={CONTACT.calendlyUrl} target="_blank" rel="noopener noreferrer" className="px-6 py-2.5 bg-blue-600 text-white hover:bg-blue-700 rounded-full font-semibold hover:shadow-lg hover:shadow-blue-600/25 transition-all duration-300 hover:scale-105">
              Book Demo
            </a>
          </div>

          <button className="md:hidden p-2 -mr-2" onClick={() => setOpen(!open)} aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden mt-4 pb-4 border-t border-slate-200 pt-4 space-y-4 animate-fadeIn bg-white max-h-[calc(100svh-5rem)] overflow-y-auto">
            <div className="space-y-1">
              <p className="text-slate-400 text-[10px] font-semibold uppercase tracking-[0.22em] pb-1">Who we build for</p>
              {INDUSTRIES.map((i) => {
                const Icon = i.icon;
                return (
                  <Link key={i.id} to={i.path} className="flex items-center gap-2.5 py-2">
                    <Icon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="text-slate-600">{i.name}</span>
                  </Link>
                );
              })}
            </div>
            <div className="space-y-1">
              <p className="text-slate-400 text-[10px] font-semibold uppercase tracking-[0.22em] pb-1">The furniture platform</p>
              {platform.map((s) => {
                const Icon = s.icon;
                return (
                  <Link key={s.slug} to={s.path} className="flex items-center gap-2.5 py-2">
                    <Icon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="text-slate-600">{s.name}</span>
                  </Link>
                );
              })}
            </div>
            <Link to="/websites#work" className="block text-slate-600 hover:text-slate-900 py-2">Our work</Link>
            <a href={section('pricing')} onClick={() => setOpen(false)} className="block text-slate-600 hover:text-slate-900 py-2">Pricing</a>
            <a href={section('faq')} onClick={() => setOpen(false)} className="block text-slate-600 hover:text-slate-900 py-2">FAQ</a>
            <Link to="/dashboard" className="block text-slate-600 hover:text-slate-900 py-2">Dashboard</Link>
            <a href={CONTACT.calendlyUrl} className="block w-full text-center px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 rounded-full font-semibold">Book Demo</a>
          </div>
        )}
      </div>
    </nav>
  );
}
